import { formatGameDuration, type FleetClient } from '@galaxis/shared';

export const SHIP_CLASS_LABELS: Record<string, string> = {
  scout: 'Felderítő',
  construction: 'Építő',
  corvette: 'Korvett',
  frigate: 'Fregatt',
  cruiser: 'Cirkáló',
};

export const SHIP_CLASS_ICONS: Record<string, string> = {
  scout: '🛰️',
  construction: '🛠️',
  corvette: '🚀',
  frigate: '🛡️',
  cruiser: '⚔️',
};

export const STATUS_LABELS: Record<string, string> = {
  idle: 'Várakozik',
  traveling: 'Úton',
  surveying: 'Felmérés',
  constructing: 'Építés',
  in_combat: 'Harcban',
  destroyed: 'Megsemmisült',
  disbanded: 'Feloszlatva',
};

export const STATUS_COLORS: Record<string, string> = {
  idle: '#94a3b8',
  traveling: '#00e5ff',
  surveying: '#ffa726',
  constructing: '#ffa726',
  in_combat: '#ef5350',
};

export type FleetFilter = 'all' | 'military' | 'survey' | 'construction';

export const FILTER_LABELS: Record<FleetFilter, string> = {
  all: 'Összes',
  military: 'Katonai',
  survey: 'Kutató',
  construction: 'Építő',
};

export function matchesFilter(fleet: FleetClient, filter: FleetFilter): boolean {
  return filter === 'all' || fleet.capabilities[filter];
}

/** One duration format for the whole game (days, hours, minutes): see packages/shared/src/universe.ts. */
export const formatDuration = formatGameDuration;

export function shipCondition(hull: number, maxHull: number): { label: string; color: string } {
  const ratio = maxHull > 0 ? hull / maxHull : 1;
  if (ratio >= 1) return { label: 'Ép', color: '#4ade80' };
  if (ratio >= 0.5) return { label: 'Sérült', color: '#fbbf24' };
  return { label: 'Kritikus', color: '#f87171' };
}

/** M10.11: a javítási panel állapota a szerver ajánlatából: folyamatban / ajánlat / nincs mit mutatni. */
export type RepairView =
  | { kind: 'open'; remainingMs: number; ships: number }
  | { kind: 'quote'; ships: number; metal: number; timeMs: number; canStart: boolean; reason: string | null }
  | { kind: 'none' };

export function repairView(
  info: { quote: { shipCount: number; metal: number; timeMs: number }; canRepair: boolean; reason: string | null; open: { completesAt: string; shipCount: number } | null } | null,
  damagedShips: number,
  now: number,
): RepairView {
  if (info?.open) return { kind: 'open', remainingMs: Math.max(0, new Date(info.open.completesAt).getTime() - now), ships: info.open.shipCount };
  if (!info || (damagedShips === 0 && info.quote.shipCount === 0)) return { kind: 'none' };
  return { kind: 'quote', ships: info.quote.shipCount, metal: info.quote.metal, timeMs: info.quote.timeMs, canStart: info.canRepair, reason: info.reason };
}

export function newIdempotencyKey(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const COMMAND_LABELS: Record<string, string> = {
  move: 'Mozgatás', survey: 'Felmérés', hold: 'Várakozás', patrol: 'Járőrözés', auto_survey: 'Automatikus felfedezés',
};

/** One-line description of what the fleet is doing right now (cards and detail). */
export function taskText(fleet: FleetClient, systemName: (id: string | null) => string): string {
  switch (fleet.status) {
    case 'traveling': return `Utazás → ${systemName(fleet.destinationSystemId)}`;
    case 'surveying': return `Felmérés: ${systemName(fleet.currentSystemId)}`;
    case 'constructing': return `Építés: ${systemName(fleet.currentSystemId)}`;
    case 'in_combat': return 'Harcban';
    default: {
      const active = fleet.commands.active;
      if (active) return `${COMMAND_LABELS[active.type] ?? active.type}${active.targetSystemId ? ` → ${systemName(active.targetSystemId)}` : ''}`;
      return fleet.commands.automation === 'patrol' ? 'Járőrözik' : fleet.commands.automation === 'auto_survey' ? 'Automatikus felfedezés' : 'Nincs aktív parancs';
    }
  }
}
